import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { User } from '../../core/models/user.model';
import { Gender } from '../../core/enums/gender.enum';
import { UserTitle } from '../../core/enums/user-title.enum';
import { UserRole } from '../../core/enums/user-role.enum';
import { ClinicService } from '../../core/services/clinic.service';
import { NotificationService } from '../../core/services/notification.service';
import { UserManagementService } from '../../core/services/user-management.service';
import { UserManagementDto } from '../../core/models/user-management.dto';

const isBrowser = typeof localStorage !== 'undefined';

@Component({
  selector: 'app-create-clinic-employee',
  imports: [FormsModule, CommonModule],
  templateUrl: './create-clinic-employee.html',
  styleUrl: './create-clinic-employee.scss',
})
export class CreateClinicEmployee implements OnInit {
  titles = Object.values(UserTitle);
  genders = Object.values(Gender);
  roles = [UserRole.RECEPTION, UserRole.MANAGER];

  users: UserManagementDto[] = [];
  isLoadingUsers = false;

  formatRole(role: string): string {
    return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
  }

  form: Partial<User> = {
    title: UserTitle.Mr,
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    gender: Gender.Male,
    role: UserRole.RECEPTION,
    city: '',
    addressLine1: '',
    addressLine2: '',
    addressLine3: '',
    postalCode: '',
  };

  isSubmitting = false;
  errorMessage: string | null = null;

  constructor(
    private clinicService: ClinicService,
    private notificationService: NotificationService,
    private router: Router,
    private userManagementService: UserManagementService,
  ) {}

  ngOnInit(): void {
    this.loadUsers();
  }

  private loadUsers(): void {
    this.isLoadingUsers = true;
    this.userManagementService.getClinicUsers().subscribe({
      next: (users) => {
        this.users = users;
        this.isLoadingUsers = false;
      },
      error: () => {
        this.users = [];
        this.isLoadingUsers = false;
      },
    });
  }

  get activeReceptionistCount(): number {
    return this.users.filter((u) => this.normalizeRole(u.role) === UserRole.RECEPTION && u.active)
      .length;
  }

  get receptionistCapReached(): boolean {
    return this.activeReceptionistCount >= 5;
  }

  private normalizeRole(role: string): UserRole {
    const normalized = (role || '').toUpperCase();
    if (normalized === UserRole.DOCTOR) return UserRole.DOCTOR;
    if (normalized === UserRole.MANAGER) return UserRole.MANAGER;
    return UserRole.RECEPTION;
  }

  submit(): void {
    this.errorMessage = null;
    this.isSubmitting = true;
    this.clinicService.createEmployee(this.form as User).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.notificationService.success('Employee created successfully!', 'Staff Registered');
        this.router.navigate(['/dashboard/manager-dashboard']);
      },
      error: (err) => {
        this.isSubmitting = false;
        const msg =
          err?.error?.message ?? 'Failed to create employee. Please verify details and try again.';
        this.errorMessage = msg;
        this.notificationService.error(msg, 'Registration Failed');
      },
    });
  }

  cancel(): void {
    this.router.navigate(['/dashboard/manager-dashboard']);
  }
}
